import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import {
  adsPlatformSearch,
  AdsDoctorBoardPage,
  DEFAULT_ADS_DOCTOR_RANGE,
  isAdsDoctorRange,
  type AdsDoctorRange,
  type AdsPlatform,
} from '@/features/agency';
import { formatDocumentTitle } from '@/lib/document-title';

interface AdsDoctorBoardSearch {
  /** Ad spend period (Meta); omitted = last 30 days (keeps the default URL clean). */
  range?: AdsDoctorRange;
  /** `google` = the Google tab; omitted = Meta. Never set to undefined (TanStack would keep the key). */
  platform?: 'google';
}

/** Every agency member may use it (the parent layout gates non-agency users; the API scopes salons). */
export const Route = createFileRoute('/_auth/agency/ads-doctor/')({
  validateSearch: (search: Record<string, unknown>): AdsDoctorBoardSearch => ({
    ...(isAdsDoctorRange(search.range) && search.range !== DEFAULT_ADS_DOCTOR_RANGE ? { range: search.range } : {}),
    ...(search.platform === 'google' ? { platform: 'google' as const } : {}),
  }),
  component: AdsDoctorBoardRoute,
});

function AdsDoctorBoardRoute() {
  const { range = DEFAULT_ADS_DOCTOR_RANGE, platform: platformParam } = Route.useSearch();
  const platform: AdsPlatform = platformParam ?? 'meta';
  const navigate = useNavigate({ from: Route.fullPath });
  // Each control changes only its own key: the range survives a platform switch and the other way round.
  const rangeSearch = (next: AdsDoctorRange) => (next === DEFAULT_ADS_DOCTOR_RANGE ? {} : { range: next });
  return (
    <>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Ads Doctor')}</title>
      </Helmet>
      <AdsDoctorBoardPage
        range={range}
        onRangeChange={(next) => void navigate({ search: { ...rangeSearch(next), ...adsPlatformSearch(platform) }, replace: true })}
        platform={platform}
        onPlatformChange={(next) => void navigate({ search: { ...rangeSearch(range), ...adsPlatformSearch(next) }, replace: true })}
      />
    </>
  );
}
